import React from 'react';
import { motion } from 'motion/react';
import ServicesBentoSection from '../components/sections/ServicesBentoSection';
import TrustedBySection from '../components/sections/TrustedBySection';
import TechStackSection from '../components/sections/TechStackSection';
import ModernPortfolioSection from '../components/sections/ModernPortfolioSection';
import DisplayCards from '../components/ui/display-cards';
import Header from '../components/Header';
import ScrollToTop from '../components/ScrollToTop';
import InlineContactForm from '../components/InlineContactForm';
import { Code2, Sparkles, Mail, MessageCircle, MapPin, Clock, Smartphone, Globe } from 'lucide-react';
import { BlurText } from '../components/ui/animated-blur-text';
import { FadeIn } from '../components/FadeIn';
import { getRecentProjects } from '../utils/portfolio';


const NewHomePage = () => {
  

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Fixed Header */}
      <Header />
      
      {/* Hero Section - Clean & Performant */}
      <section className="relative h-screen overflow-hidden bg-background">
        {/* Lightweight gradient background */}
        <div className="absolute inset-0">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-zinc-800/30 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-zinc-700/20 rounded-full blur-[120px]" />
          <div className="absolute top-[40%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-[40%] h-[40%] bg-primary/5 rounded-full blur-[100px]" />
        </div>

        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        />

        {/* Hero Content */}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center px-4"
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/80 border border-zinc-800 mb-8"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-medium text-zinc-400">
                프로젝트 접수 가능
              </span>
            </motion.div>

            {/* Main Title */}
            <div className="mb-8">
              <motion.h1
                className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight"
                initial={{ opacity: 0, filter: "blur(10px)" }}
                animate={{ opacity: 1, filter: "blur(0px)" }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                <span className="text-foreground">Nalda</span>
                <span className="text-zinc-500 font-semibold">Dev</span>
              </motion.h1>
            </div>

            {/* Subtitle */}
            <motion.p
              className="text-xl sm:text-2xl md:text-3xl text-zinc-400 mb-6 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              고객님의 비전을 함께 성장시킬 수 있는 개발 파트너
            </motion.p>

            {/* Credentials */}
            <motion.div
              className="flex items-center justify-center gap-4 mb-12 text-sm text-zinc-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              <span>공군 개발병 출신</span>
              <span className="text-zinc-700">•</span>
              <span>해양수산부 장관상 수상</span>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              className="flex gap-4 justify-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              <motion.a
                href="#contact"
                className="bg-white text-black font-medium hover:bg-zinc-100 shadow-lg hover:shadow-xl transition-all rounded-full px-8 py-3 text-lg inline-block"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                프로젝트 시작하기
              </motion.a>
              <motion.a
                href="#portfolio"
                className="border border-zinc-800 bg-zinc-900/50 text-zinc-300 hover:bg-zinc-800 hover:text-white hover:border-zinc-700 transition-all rounded-full px-8 py-3 text-lg inline-block"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                작업물 보기
              </motion.a>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="text-zinc-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </motion.div>
        </motion.div>
      </section>

      {/* Trusted By Section with Partners Marquee */}
      <TrustedBySection />

      {/* About Section */}
      <section id="about" className="py-32 px-4 bg-background relative">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn className="mb-8" delay={0}>
            <BlurText 
              text="검증된 개발 전문가"
              className="text-4xl md:text-5xl font-bold"
              duration={0.8}
            />
          </FadeIn>
          <FadeIn delay={200}>
            <p className="text-xl text-foreground/70 leading-relaxed mb-12">
              공군 프로그램 개발병으로 복무한 경험과 개발 관련 공모전에서 <span className="text-foreground font-semibold">해양수산부 장관상</span>을 수상한 전문성을 바탕으로,
              디데이 프로그램을 직접 개발하여 윈도우, MacOS, 안드로이드, iOS 등 다양한 플랫폼에 출시했으며,
              <span className="text-foreground font-semibold">일일 활성 사용자(DAU) 2,500명</span> 규모의 서비스 운영 경험을 보유하고 있습니다.
            </p>
          </FadeIn>
          {/* Team Members */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16 max-w-3xl mx-auto">
            {[
              {
                name: "박성재",
                role: "대표 / 개발, 디자인",
                university: "서울과학기술대학교",
                description: "풀스택 개발과 UI/UX 디자인을 통합적으로 다루며, 기술과 디자인의 균형을 추구합니다.",
                gradient: "from-zinc-700 to-zinc-800",
                skills: ["React", "Vue", "Node.js", "Figma"]
              },
              {
                name: "이정원",
                role: "개발",
                university: "한양대학교",
                description: "백엔드 아키텍처와 시스템 설계에 특화되어 있으며, 안정적인 서비스 구축에 집중합니다.",
                gradient: "from-zinc-600 to-zinc-700",
                skills: ["Spring", "Python", "AWS", "Docker"]
              }
            ].map((member, index) => (
              <FadeIn key={member.name} delay={index * 150}>
                <motion.div
                  className="relative bg-zinc-950 rounded-2xl border border-zinc-800 overflow-hidden group"
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {/* Gradient accent line */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${member.gradient}`} />
                  
                  {/* Background gradient on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${member.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                  
                  {/* Content */}
                  <div className="relative p-8">
                    <div className="mb-6">
                      <h3 className="text-2xl font-bold text-white mb-1">{member.name}</h3>
                      <p className="text-sm text-zinc-400 mb-2">{member.university}</p>
                      <p className={`text-sm font-medium bg-gradient-to-r ${member.gradient} bg-clip-text text-transparent`}>
                        {member.role}
                      </p>
                    </div>
                    
                    <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                      {member.description}
                    </p>
                    
                    {/* Skills */}
                    <div className="flex flex-wrap gap-2">
                      {member.skills.map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-1 text-xs font-medium bg-zinc-800/50 text-zinc-400 rounded-full border border-zinc-700/50"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Subtle pattern */}
                  <div className="absolute bottom-0 right-0 w-32 h-32 opacity-5">
                    <div className={`w-full h-full bg-gradient-to-tl ${member.gradient} rounded-full blur-3xl`} />
                  </div>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Showcase */}
      <section className="py-32 px-4 bg-background overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="text-center mb-16" delay={300}>
            <BlurText 
              text="최근 프로젝트"
              className="text-4xl md:text-5xl font-bold mb-4"
              duration={0.8}
              as="h2"
            />
            <p className="text-xl text-foreground/70">
              성공적으로 완료한 최신 프로젝트들
            </p>
          </FadeIn>

          <FadeIn delay={400} className="flex justify-center">
            <DisplayCards 
              cards={getRecentProjects(3).map((project, index) => {
                const getCategoryIcon = () => {
                  switch (project.category) {
                    case 'mobile': return <Smartphone className="size-4 text-zinc-300" />;
                    case 'web': return <Globe className="size-4 text-zinc-300" />;
                    case 'solution': return <Code2 className="size-4 text-zinc-300" />;
                    default: return <Sparkles className="size-4 text-zinc-300" />;
                  }
                };
                
                const getMetricDisplay = () => {
                  if (project.metrics.dau) return `DAU ${project.metrics.dau}`;
                  if (project.metrics.traffic) return project.metrics.traffic;
                  return project.period;
                };
                
                const positionClasses = [
                  "[grid-area:stack] hover:-translate-y-10 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0",
                  "[grid-area:stack] translate-x-16 translate-y-10 hover:-translate-y-1 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration:700 hover:grayscale-0 before:left-0 before:top-0",
                  "[grid-area:stack] translate-x-32 translate-y-20 hover:translate-y-10"
                ];
                
                return {
                  icon: getCategoryIcon(),
                  title: project.title,
                  description: project.description,
                  date: getMetricDisplay(),
                  iconClassName: "text-zinc-500",
                  titleClassName: "text-zinc-300",
                  className: positionClasses[index] || positionClasses[2],
                };
              })}
            />
          </FadeIn>
        </div>
      </section>

      {/* Services Section with Dark Theme */}
      <section id="services" className="bg-muted/20">
        <ServicesBentoSection />
      </section>

      {/* Tech Stack Section */}
      <TechStackSection />

      {/* Portfolio Section with Modern Design */}
      <ModernPortfolioSection />

      {/* Contact Section - Inline Form */}
      <section id="contact" className="relative py-32 px-4 bg-gradient-to-b from-background via-muted/5 to-background">
        <div className="max-w-4xl mx-auto">
          <FadeIn className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/80 border border-zinc-800 mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-medium text-zinc-400">프로젝트 접수 가능</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <BlurText
                text="프로젝트 문의"
                className="text-4xl md:text-5xl font-bold"
                duration={0.6}
              />
            </h2>
            <p className="text-lg text-zinc-400">
              간단한 정보만으로 바로 문의하실 수 있습니다
              <br />
              <span className="text-zinc-500 text-base">평균 응답 시간: 30분 이내</span>
            </p>
          </FadeIn>

          <FadeIn delay={200}>
            <InlineContactForm />
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-zinc-950 border-t border-zinc-900">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 pointer-events-none" />
        
        <div className="relative max-w-7xl mx-auto px-4 py-16">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            {/* Brand Section */}
            <div className="lg:col-span-4">
              <div className="mb-6">
                <span className="text-2xl font-bold text-white">
                  Nalda<span className="text-zinc-500">Dev</span>
                </span>
              </div>
              <p className="text-zinc-400 mb-6 leading-relaxed text-sm">
                최신 기술과 창의적인 아이디어로<br />
                고객님의 비즈니스를 성공으로 이끌어드립니다.
              </p>
              
              {/* Social Links */}
              <div className="flex gap-3">
                <a 
                  href="https://github.com/naldadev" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-zinc-900/50 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-zinc-700 hover:bg-zinc-900 transition-all"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>
                <a 
                  href="http://pf.kakao.com/_XSxhNn/chat" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-zinc-900/50 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-yellow-400 hover:border-yellow-400/20 hover:bg-yellow-400/10 transition-all"
                >
                  <svg className="w-4 h-4" viewBox="0 0 256 256" fill="currentColor">
                    <path d="M128 36C70.562 36 24 72.713 24 118c0 29.279 19.466 54.97 48.748 69.477l-10.534 38.883c-.905 3.34 2.998 5.99 5.756 3.905l46.725-33.452C120.077 197.905 125.975 198.5 128 198.5c57.438 0 104-34.213 104-80.5S185.438 36 128 36"/>
                  </svg>
                </a>
              </div>
            </div>
            
            {/* Quick Links */}
            <div className="lg:col-span-2">
              <h4 className="text-white font-medium text-sm mb-4">서비스</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-zinc-500 hover:text-zinc-300 transition-colors text-sm">웹 개발</a></li>
                <li><a href="#services" className="text-zinc-500 hover:text-zinc-300 transition-colors text-sm">모바일 앱</a></li>
                <li><a href="#services" className="text-zinc-500 hover:text-zinc-300 transition-colors text-sm">UI/UX 디자인</a></li>
                <li><a href="#services" className="text-zinc-500 hover:text-zinc-300 transition-colors text-sm">AI 솔루션</a></li>
              </ul>
            </div>
            
            <div className="lg:col-span-2">
              <h4 className="text-white font-medium text-sm mb-4">회사</h4>
              <ul className="space-y-2">
                <li><a href="#about" className="text-zinc-500 hover:text-zinc-300 transition-colors text-sm">회사 소개</a></li>
                <li><a href="#portfolio" className="text-zinc-500 hover:text-zinc-300 transition-colors text-sm">포트폴리오</a></li>
                <li><a href="#tech" className="text-zinc-500 hover:text-zinc-300 transition-colors text-sm">기술 스택</a></li>
                <li><a href="#contact" className="text-zinc-500 hover:text-zinc-300 transition-colors text-sm">문의하기</a></li>
              </ul>
            </div>
            
            {/* Contact Info */}
            <div className="lg:col-span-4">
              <h4 className="text-white font-medium text-sm mb-4">문의하기</h4>
              <div className="space-y-3">
                <a 
                  href="mailto:contact@nalda.dev" 
                  className="flex items-center gap-2 text-zinc-500 hover:text-zinc-300 transition-colors text-sm"
                >
                  <Mail className="w-4 h-4" />
                  <span>contact@nalda.dev</span>
                </a>
                <a 
                  href="http://pf.kakao.com/_XSxhNn/chat" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-zinc-500 hover:text-zinc-300 transition-colors text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>카카오톡 오픈채팅</span>
                </a>
                <div className="flex items-center gap-2 text-zinc-500 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>서울 노원구 / 전국 출장 가능</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-500 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>24시간 연락 가능</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="pt-8 border-t border-zinc-800/50">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-zinc-600 text-xs">
                &copy; 2025 NaldaDev. All rights reserved.
              </p>
              <div className="flex gap-6 text-xs">
                <a href="/privacy" className="text-zinc-600 hover:text-zinc-400 transition-colors">개인정보처리방침</a>
                <a href="#" className="text-zinc-600 hover:text-zinc-400 transition-colors">이용약관</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
};

export default NewHomePage;